import { action, makeObservable, observable, runInAction } from 'mobx';

import DispatchSearchParam from '~components/Order/Selectors/DispatchSearchParam';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Order } from '~hooks/useOrders/models';
import { PhaseBasic } from '~hooks/useProjects/models';
import { getOrderStatesPerStatus } from '~pages/Dispatch/utils';
import { Pagination, PaginationLink } from '~services/pagination';
import { RootStore } from '~store/RootStore';
import { getPageNumber } from '~utils/pagination';

const DEFAULT_PAGINATION_STATE = {
  limit: MAX_API_PAGE_SIZE,
  after: '',
  before: '',
  page: 1,
};

class OrdersStore {
  orders: Order[] = [];
  childCompanyOrders: Order[] = [];
  // Keep cloned orders in a separate list, as we need them to be present in the store even if they are not
  // Visible to the user in any grid
  clonedOrders: Order[] = [];
  ordersPagination: Pagination = DEFAULT_PAGINATION_STATE;
  childCompanyOrdersPagination: Pagination = DEFAULT_PAGINATION_STATE;
  dispatchFilter: Record<string, any> = {};
  orderFilter: Record<string, any> = {};
  areFilterOptionsLoaded: boolean = false;
  orderUpdatesOccurred: boolean = false;
  acceptNewOrders: boolean = true;
  pendingNewOrders: boolean = false;
  phaseTypeheadsByOrderID: Record<string, PhaseBasic[]> = {};

  constructor(private readonly rootStore: RootStore) {
    makeObservable(this, {
      orders: observable,
      childCompanyOrders: observable,
      clonedOrders: observable,
      ordersPagination: observable,
      childCompanyOrdersPagination: observable,
      dispatchFilter: observable,
      orderFilter: observable,
      areFilterOptionsLoaded: observable,
      orderUpdatesOccurred: observable,
      acceptNewOrders: observable,
      pendingNewOrders: observable,
      phaseTypeheadsByOrderID: observable,
      setPhaseTypeheadsByOrderID: action,
    });
    this.rootStore = rootStore;
  }

  setOrdersPagination(pagination: Pagination) {
    this.ordersPagination = {
      ...this.ordersPagination,
      before: pagination.before || '',
      after: pagination.after || '',
    };
  }

  updateOrdersPageNumber(link: PaginationLink) {
    runInAction(() => {
      this.ordersPagination = {
        ...this.ordersPagination,
        page: getPageNumber(this.ordersPagination.page, link),
      };
    });
  }

  setChildCompanyOrdersPagination(pagination: Pagination) {
    this.childCompanyOrdersPagination = {
      ...this.childCompanyOrdersPagination,
      before: pagination.before || '',
      after: pagination.after || '',
    };
  }

  updateChildCompanyOrdersPageNumber(link: PaginationLink) {
    runInAction(() => {
      this.childCompanyOrdersPagination = {
        ...this.childCompanyOrdersPagination,
        page: getPageNumber(this.childCompanyOrdersPagination.page, link),
      };
    });
  }

  setOrders(orders: Order[]) {
    this.orders = [...orders];
  }

  setChildCompanyOrders(orders: Order[]) {
    this.childCompanyOrders = [...orders];
  }

  deleteOrder(id: string) {
    this.orders = this.orders.filter((o) => o.id !== id);
  }

  updateOrder(order: Order) {
    this.orders = this.orders.map((o) => (o.id === order.id ? order : o));
  }

  updateChildCompanyOrder(order: Order) {
    this.childCompanyOrders = this.childCompanyOrders.map((o) =>
      o.id === order.id ? order : o,
    );
  }

  updateClonedOrder(order: Order) {
    this.clonedOrders = this.clonedOrders.map((o) => (o.id === order.id ? order : o));
  }

  addOrder(order: Order) {
    const idx = this.orders.findIndex((o) => o.id === order.id);
    if (idx !== -1) {
      this.updateOrder(order);
      // Check if we are accepting new orders, if not (when filters other than states are applied),
      // We should not add the order to the list
    } else if (matchedOnFilter(order, this.dispatchFilter)) {
      if (this.acceptNewOrders) {
        this.orders = [...this.orders, order];
      } else {
        this.setPendingNewOrders(true);
      }
    }
  }

  addChildCompanyOrder(order: Order) {
    const idx = this.childCompanyOrders.findIndex((o) => o.id === order.id);
    if (idx !== -1) {
      this.updateOrder(order);
      // Check if we are accepting new orders, if not (when filters other than states are applied),
      // We should not add the order to the list
    } else if (matchedOnFilter(order, this.dispatchFilter)) {
      if (this.acceptNewOrders) {
        this.childCompanyOrders = [...this.childCompanyOrders, order];
      } else {
        this.setPendingNewOrders(true);
      }
    }
  }

  addClonedOrder = action((order: Order) => {
    const idx = this.clonedOrders.findIndex((o) => o.id === order.id);
    if (idx !== -1) {
      this.updateClonedOrder(order);
    } else {
      this.clonedOrders = [...this.clonedOrders, order];
    }
  });

  setDispatchFilter(filter: Record<string, any>) {
    this.dispatchFilter = filter;
  }

  addDispatchFilter(filter: Record<string, any>) {
    this.dispatchFilter = {
      ...this.dispatchFilter,
      ...filter,
    };
  }

  setOrderFilter(filter: Record<string, any>) {
    this.orderFilter = filter;
  }

  addOrderFilter(filter: Record<string, any>) {
    this.orderFilter = {
      ...this.orderFilter,
      ...filter,
    };
  }

  setFilterOptionsLoaded = (loaded: boolean) => {
    this.areFilterOptionsLoaded = loaded;
  };

  setOrderUpdatesOccurred = (updatesMade: boolean) => {
    this.orderUpdatesOccurred = updatesMade;
  };

  setAcceptNewOrders = (acceptNewOrders: boolean) => {
    this.acceptNewOrders = acceptNewOrders;
  };
  setPendingNewOrders = (pendingNewOrders: boolean) => {
    this.pendingNewOrders = pendingNewOrders;
  };

  setPhaseTypeheadsByOrderID = (orderID: string, phases: PhaseBasic[]) => {
    this.phaseTypeheadsByOrderID = {
      ...this.phaseTypeheadsByOrderID,
      [orderID]: phases,
    };
  };
}

const matchedOnFilter = (order: Order, filter: Record<string, any>) => {
  return Object.keys(filter).every((key: string) => {
    const filterValue = filter[key][0];

    if (key === DispatchSearchParam.STATUS) {
      const orderStates = getOrderStatesPerStatus(filterValue);
      return orderStates.length === 0 || orderStates.includes(order.state);
    } else {
      return true;
    }
  });
};

export default OrdersStore;
